<template>
  <page-outline :title="$t('mandate.fraud_case.title')">
    <template slot="pageContent">
      <b-container fluid>
        <b-row class="mt-4">
          <b-col cols="auto" style="width: fit-content;">
            <div class="back-button-wrapper">
              <back-button></back-button>
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-3" v-if="caseData">
          <b-col cols="12">
            <b-card>
              <div class="case-header mb-4">
                <h3 class="mb-2 mb-md-0">{{ translateFraudValue(caseData.type) }}</h3>
                <b-badge :variant="severityVariant(caseData.severity)" class="case-severity-badge">
                  {{ translateFraudValue(caseData.severity) }} {{ $t('mandate.fraud_case.severity') }}
                </b-badge>
              </div>

              <b-row>
                <b-col cols="12" md="4" class="mb-3">
                  <div class="kpi-card">
                    <span class="kpi-label">{{ $t('mandate.fraud_case.detected_by') }}</span>
                    <strong class="kpi-value kpi-value-small">{{ translateFraudValue(caseData.detectedBy) }}</strong>
                  </div>
                </b-col>
                <b-col cols="12" md="4" class="mb-3">
                  <div class="kpi-card">
                    <span class="kpi-label">{{ $t('mandate.fraud_case.amount_at_risk') }}</span>
                    <strong class="kpi-value">{{ caseData.amountLabel }}</strong>
                  </div>
                </b-col>
                <b-col cols="12" md="4" class="mb-3">
                  <div class="kpi-card">
                    <span class="kpi-label">{{ $t('mandate.fraud_case.detection_date') }}</span>
                    <strong class="kpi-value kpi-value-small">{{ caseData.date }}</strong>
                  </div>
                </b-col>
              </b-row>

              <hr class="my-4" />

              <h5 class="mb-3">{{ $t('mandate.fraud_case.case_information') }}</h5>
              <b-row>
                <b-col cols="12" md="6" class="mb-3">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.fraud_case.sncf_entity') }}</span>
                  <p class="detail-value">{{ caseData.entity }}</p>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.fraud_case.fraud_nature') }}</span>
                  <p class="detail-value">{{ translateFraudValue(caseData.type) }}</p>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.fraud_case.related_id') }}</span>
                  <p class="detail-value"><code>{{ caseData.relatedId }}</code></p>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.fraud_case.current_status') }}</span>
                  <b-badge :variant="statusVariant(caseData.status)">{{ translateFraudValue(caseData.status) }}</b-badge>
                </b-col>
              </b-row>

              <hr class="my-4" />

              <h5 class="mb-3">{{ $t('mandate.fraud_case.detection_details') }}</h5>
              <p class="detail-description">{{ translateFraudValue(caseData.description) }}</p>

              <hr class="my-4" />

              <h5 class="mb-3">{{ $t('mandate.fraud_case.investigation_timeline') }}</h5>
              <div class="table-responsive">
                <b-table
                  striped hover small
                  :items="translatedTimeline"
                  :fields="timelineFields"
                  class="mb-0"
                >
                  <template #cell(actor)="data">
                    <b-badge variant="secondary">{{ translateFraudValue(data.value) }}</b-badge>
                  </template>
                </b-table>
              </div>

              <template v-if="relatedCases.length">
                <hr class="my-4" />
                <h5 class="mb-3">{{ $t('mandate.fraud_case.related_cases_for_entity') }}</h5>
                <div class="table-responsive">
                  <b-table
                    striped hover small
                    :items="relatedCases"
                    :fields="relatedCasesFields"
                    class="mb-0 clickable-rows"
                    @row-clicked="goToRelatedCase"
                  >
                    <template #cell(severity)="data">
                      <b-badge :variant="severityVariant(data.value)">{{ translateFraudValue(data.value) }}</b-badge>
                    </template>
                  </b-table>
                </div>
              </template>

              <b-row class="mt-4">
                <b-col cols="12" class="d-flex justify-content-end">
                  <b-button class="btnFilter_2 mr-2" @click="markResolved" v-if="caseData.status !== 'Resolved'">
                    Marquer comme résolu
                  </b-button>
                  <b-button class="btnFilter_2" @click="exportCase">
                    <i class="fa-light fa-upload"></i>
                    {{ $t('mandate.fraud_case.export_case_pdf') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mt-3" v-else>
          <b-col cols="12">
            <b-card class="text-center py-5">
              <span class="noResult">{{ $t('mandate.fraud_case.not_found') }}</span>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import BackButton from '@/components/common/backButton.vue'

export default {
  name: 'FraudCaseDetails',
  components: {
    'page-outline': PageOutline,
    'back-button': BackButton
  },
  props: {
    caseId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      // mock detail par cas, a brancher sur le vrai back fraude une fois l'API dispo
      fraudCases: {
        1: {
          date: '2026-07-10',
          type: 'Multiple mandates same debtor',
          entity: 'SNCF Voyageurs',
          severity: 'High',
          detectedBy: 'SNCF',
          amount: 1240,
          amountLabel: '€1,240.00',
          relatedId: 'MDT-2026-004821',
          status: 'Under investigation',
          description: 'Three active mandates were created within 48 hours using the same debtor IBAN but slightly different debtor names, flagged by the duplicate-detection engine.',
          timeline: [
            { date: '2026-07-10 09:14', action: 'Case opened', actor: 'System' },
            { date: '2026-07-10 11:30', action: 'Assigned to fraud analyst', actor: 'SNCF' },
            { date: '2026-07-11 14:02', action: 'Debtor contacted for verification', actor: 'SNCF' }
          ]
        },
        2: {
          date: '2026-07-11',
          type: 'Unusual amount pattern',
          entity: 'SNCF Reseau',
          severity: 'Medium',
          detectedBy: 'Bank',
          amount: 680,
          amountLabel: '€680.00',
          relatedId: 'COL-2026-118834',
          status: 'Under investigation',
          description: 'Debtor bank flagged an unusual sequence of collection amounts inconsistent with the historical payment pattern on this account.',
          timeline: [
            { date: '2026-07-11 08:05', action: 'Alert received from bank', actor: 'Bank' },
            { date: '2026-07-11 10:20', action: 'Case opened', actor: 'System' }
          ]
        },
        3: {
          date: '2026-07-12',
          type: 'IBAN mismatch attempt',
          entity: 'SNCF Voyageurs',
          severity: 'High',
          detectedBy: 'Bank',
          amount: 2150,
          amountLabel: '€2,150.00',
          relatedId: 'MDT-2026-004902',
          status: 'Resolved',
          description: 'Attempted mandate amendment with an IBAN not matching the registered debtor identity was blocked by the debtor bank prior to execution.',
          timeline: [
            { date: '2026-07-12 07:40', action: 'Alert received from bank', actor: 'Bank' },
            { date: '2026-07-12 09:15', action: 'Mandate amendment blocked', actor: 'Bank' },
            { date: '2026-07-12 16:00', action: 'Case closed - no financial impact', actor: 'SNCF' }
          ]
        },
        4: {
          date: '2026-07-12',
          type: 'Rapid mandate creation/cancellation',
          entity: 'Rail Logistics Europe',
          severity: 'Low',
          detectedBy: 'SNCF',
          amount: 310,
          amountLabel: '€310.00',
          relatedId: 'MDT-2026-004915',
          status: 'Monitoring',
          description: 'A mandate was created and cancelled twice within the same day, a pattern consistent with testing stolen payment credentials.',
          timeline: [
            { date: '2026-07-12 13:02', action: 'Case opened', actor: 'System' },
            { date: '2026-07-12 13:45', action: 'Added to monitoring watchlist', actor: 'SNCF' }
          ]
        },
        5: {
          date: '2026-07-13',
          type: 'Stolen identity suspicion',
          entity: 'SNCF Voyageurs',
          severity: 'High',
          detectedBy: 'Bank',
          amount: 1890,
          amountLabel: '€1,890.00',
          relatedId: 'MDT-2026-004960',
          status: 'Under investigation',
          description: 'Debtor bank reported that the identity documents used to open the debited account do not match the identity on file for that customer.',
          timeline: [
            { date: '2026-07-13 06:55', action: 'Alert received from bank', actor: 'Bank' },
            { date: '2026-07-13 09:30', action: 'Case opened', actor: 'System' },
            { date: '2026-07-13 11:00', action: 'Assigned to fraud analyst', actor: 'SNCF' }
          ]
        }
      }
    }
  },
  computed: {
    caseData () {
      const id = this.caseId || this.$f7route?.params?.caseId
      return this.fraudCases[id] || null
    },
    translatedTimeline () {
      if (!this.caseData) return []
      return this.caseData.timeline.map(item => ({
        ...item,
        action: this.translateFraudValue(item.action),
        actor: this.translateFraudValue(item.actor)
      }))
    },
    relatedCases () {
      if (!this.caseData) return []
      const currentId = this.caseId || this.$f7route?.params?.caseId
      return Object.entries(this.fraudCases)
        .filter(([id, c]) => c.entity === this.caseData.entity && String(id) !== String(currentId))
        .map(([id, c]) => ({
          caseId: id,
          date: c.date,
          type: this.translateFraudValue(c.type),
          severity: c.severity,
          status: this.translateFraudValue(c.status)
        }))
    },
    relatedCasesFields () {
      return [
        { key: 'date', label: this.$t('mandate.fraud_case.date'), sortable: true },
        { key: 'type', label: this.$t('mandate.fraud_case.fraud_nature'), sortable: true },
        { key: 'severity', label: this.$t('mandate.fraud_case.severity'), sortable: true },
        { key: 'status', label: this.$t('mandate.fraud_case.status'), sortable: true }
      ]
    },
    timelineFields () {
      return [
        { key: 'date', label: this.$t('mandate.fraud_case.date'), sortable: true },
        { key: 'action', label: this.$t('mandate.fraud_case.action'), sortable: true },
        { key: 'actor', label: this.$t('mandate.fraud_case.performed_by'), sortable: true }
      ]
    }
  },
  methods: {
    translateFraudValue (value) {
      if (value === null || value === undefined || value === '') return value
      const key = 'mandate.fraud_case.' + String(value)
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    severityVariant (severity) {
      if (severity === 'High') return 'danger'
      if (severity === 'Medium') return 'warning'
      return 'secondary'
    },
    statusVariant (status) {
      if (status === 'Resolved') return 'success'
      if (status === 'Under investigation') return 'danger'
      if (status === 'Monitoring') return 'warning'
      return 'secondary'
    },
    goToRelatedCase (row) {
      this.$f7.mainView.router.load({ url: '/fraud-case/' + row.caseId })
    },
    markResolved () {
      // a brancher sur le vrai endpoint de mise a jour de statut
      if (this.caseData) this.caseData.status = 'Resolved'
    },
    exportCase () {
      window.print()
    }
  }
}
</script>

<style scoped>
::v-deep .back-button-wrapper,
::v-deep .back-button {
  width: auto !important;
  display: inline-block !important;
}

.case-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.case-severity-badge {
  font-size: 0.9rem;
  padding: 0.4rem 0.9rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
  text-decoration: none;
}

@media (min-width: 768px) {
  .case-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}


.kpi-card {
  min-height: 90px;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kpi-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.45rem;
}

.kpi-value {
  font-size: 1.5rem;
  color: var(--actionButtonColorBlue);
}

.kpi-value-small {
  font-size: 1.05rem;
}

.detail-value {
  font-size: 1rem;
  color: var(--actionTextColorBlack);
  margin-bottom: 0;
}

.detail-description {
  color: #4b5563;
  line-height: 1.5;
}

::v-deep .table-responsive table thead th {
  text-align: center;
  vertical-align: middle;
}

::v-deep .table-responsive table tbody td {
  text-align: center;
  vertical-align: middle;
}
</style>
